/**
 * Converts a decimal currency value (e.g., 10.50 EUR) to minor units (e.g., 1050)
 * @example currencyToCents(10.5) // returns 1050
 */
export const currencyToCents = (value: number): number => Math.round(value * 100);

/**
 * Converts minor units (e.g., 1050) to a decimal currency value (e.g., 10.50)
 * @example centsToCurrency(1050) // returns 10.5
 */
export const centsToCurrency = (cents: number): number => Number((cents / 100).toFixed(2));

const numberFormatCache = new Map<string, Intl.NumberFormat>();

/**
 * Formats a minor-units amount as a localized currency string.
 * The underlying `Intl.NumberFormat` instances are memoized per
 * locale/currency pair — safe to call from change detection.
 * @example formatCents(1050, 'EUR', 'fr') // returns "10,50 €"
 */
export function formatCents(cents: number, currencyCode: string, locale?: string): string {
  const cacheKey = `${locale ?? ''}:${currencyCode}`;
  let numberFormat = numberFormatCache.get(cacheKey);

  if (!numberFormat) {
    numberFormat = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
    });
    numberFormatCache.set(cacheKey, numberFormat);
  }

  return numberFormat.format(centsToCurrency(cents));
}
